import type {
  Attachment,
  Severity,
  ChecklistItem,
  ChecklistItemStatus,
  ChecklistState,
  CreateFindingBody,
  Finding,
  FindingComment,
  FindingStatus,
  ItemChatThread,
  QaSession,
  TesterStatus,
  WidgetConfig,
} from "../shared/schemas";

/**
 * API client + offline outbox.
 *
 * Capture must never block on the network: a POST that fails at the transport
 * level is queued in localStorage and flushed on reconnect. Every queued
 * finding carries a `client_id`, and the server treats a repeat of a known
 * `client_id` as a no-op — so retrying can never duplicate a finding.
 *
 * The distinction that makes it work: a 4xx is a real rejection the tester must
 * see (bad token, revoked session), while a network error is "not yet". Only
 * the second is queued.
 *
 * Ported from qa-deck's `src/widget/api.ts`; endpoints moved under `/api/qa/*`
 * and the storage keys are namespaced per deck origin, so a tester working on
 * two customer sites in one browser doesn't cross their tokens.
 */

const TOKEN_KEY = "qa-deck.token";
const SESSION_KEY = "qa-deck.session";
const OUTBOX_KEY = "qa-deck.outbox";
const PANEL_KEY = "qa-deck.panel";

let baseUrl = "";
/** Suffix that scopes localStorage keys to one deck deployment. */
let storageScope = "";

export function setBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
  storageScope = `:${baseUrl}`;
}

export function getBaseUrl(): string {
  return baseUrl;
}

function key(base: string): string {
  return `${base}${storageScope}`;
}

function readLocal(name: string): string | null {
  try {
    return window.localStorage.getItem(key(name));
  } catch {
    return null; // storage blocked (private mode / third-party restrictions)
  }
}

function writeLocal(name: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key(name));
    else window.localStorage.setItem(key(name), value);
  } catch {
    /* ignore */
  }
}

export function getToken(): string | null {
  return readLocal(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  writeLocal(TOKEN_KEY, token);
}

export function getStoredSession(): QaSession | null {
  const raw = readLocal(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as QaSession;
  } catch {
    return null;
  }
}

export function setStoredSession(session: QaSession | null): void {
  writeLocal(SESSION_KEY, session ? JSON.stringify(session) : null);
}

/**
 * Whether the panel was open the last time the tester touched it.
 *
 * A scenario's steps routinely cross into another page, and some of those
 * crossings are a full document load — which reboots the widget and drops the
 * open/closed flag with everything else in memory. The column then vanishes at
 * exactly the moment the tester is mid-scenario and needs to read the next
 * step. Scoped per deck deployment like the token and the session pointer, and
 * a blocked localStorage degrades to the old behaviour (closed).
 */
export function getPanelOpen(): boolean {
  return readLocal(PANEL_KEY) === "1";
}

export function setPanelOpen(open: boolean): void {
  writeLocal(PANEL_KEY, open ? "1" : null);
}

/**
 * "Open THIS finding" — what an inbox row is pointing at.
 *
 * Not storage and not a navigation: the card is FETCHED by id
 * (`getFinding`) and shown in the Findings tab wherever the tester happens to
 * be. It used to be a one-shot localStorage pointer plus a jump to the page the
 * finding was filed on, which cannot work at all when that page is on another
 * origin — a tester on localhost answering a claim about a finding filed on
 * staging, which is the ordinary case rather than the edge one.
 */
export interface FindingFocus {
  id: string;
  /** Where it was filed. Shown on the pinned card, so the tester knows the
   *  words in front of them describe another environment. */
  url: string;
  /** Open the conversation too — what a reply row wants, and a fix claim does
   *  not: that one is answered by the buttons on the card. */
  thread?: boolean;
  /**
   * "Retest it" set this — the finding is IN HAND until answered.
   *
   * A reply row opens a card; a retest row starts a job that spans page loads,
   * and the pin (below) exists for that job alone. The flag is what tells the
   * panel to hold the card across reloads and to let go of it once the finding
   * is no longer `fixed`.
   */
  retest?: boolean;
  /**
   * Set with the pin and cleared by the first boot that reads it. That boot
   * opens the panel on the Findings tab and scrolls to the card — the arrival
   * the tester asked for. Every later reload restores the card QUIETLY: same
   * tab, but the panel's open-state is left as the tester had it and nothing
   * scrolls, or a form that reloads the page mid-scenario would pop the panel
   * open each time.
   */
  fresh?: boolean;
  /**
   * Opened from a link in a tester digest (`#qadeck=<id>`). The mail cannot
   * know what the finding will be by the time it is clicked, so the panel
   * decides on fetch: a `fixed` one becomes a held retest pin, exactly as
   * "Retest it" would have made it; a reply opens its thread.
   */
  linked?: boolean;
}

/**
 * `#qadeck=<finding id or QA-ref>` — the link a tester digest puts on every
 * finding. Read once at boot and REMOVED from the address bar, so a reload or
 * a copied URL does not re-open it. Validated against the two shapes a finding
 * can be addressed by; anything else is left alone, since the fragment belongs
 * to the customer's page and may mean something to it.
 */
const LINK_RE = /^#qadeck=(fnd_[A-Za-z0-9_-]{4,64}|QA-\d{1,9})$/;

export function takeLinkedFinding(): FindingFocus | null {
  let match: RegExpExecArray | null;
  try {
    match = LINK_RE.exec(decodeURIComponent(location.hash));
  } catch {
    return null;
  }
  if (!match) return null;
  const url = location.href.split("#")[0];
  try {
    history.replaceState(history.state, "", url);
  } catch {
    // A sandboxed frame may refuse; the card still opens, the fragment stays.
  }
  return { id: match[1], url, linked: true };
}

const FOCUS_KEY = "qa-deck.focus";

/**
 * "This finding is in hand — show it wherever I land."
 *
 * Set by "Retest it", which navigates to the page the finding was filed on: the
 * widget on the other side is a fresh boot that never saw the click, so the
 * request has to outlive the document load. Same scoped storage as the token
 * and the session pointer.
 *
 * NOT one-shot. It was, once — read on arrival and cleared — and the card died
 * the first time a retest involved anything that reloads the document: a form
 * that POSTs and redirects, a login bounce, the return from a payment page. The
 * panel came back open and empty, and the tester went to the inbox to find the
 * job they were in the middle of. So the pin is held until RELEASED, and a
 * release is an act, never a side effect of moving around: the verdict,
 * "Close" on the pinned card, or the finding no longer being `fixed` (somebody
 * else answered it, the claim was withdrawn, it is gone). Never a timeout — a
 * tester back from lunch mid-retest should find the same card.
 *
 * One pin at a time: a retest is one thing in hand, and pressing "Retest it"
 * on another row replaces it.
 */
export function setFindingFocus(focus: FindingFocus | null): void {
  writeLocal(FOCUS_KEY, focus ? JSON.stringify(focus) : null);
}

/** The held pin, or null. Reading does not release it. */
export function getFindingFocus(): FindingFocus | null {
  const raw = readLocal(FOCUS_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as FindingFocus;
    return typeof parsed?.id === "string" && parsed.id ? parsed : null;
  } catch {
    return null;
  }
}

/** Let go of the pin — only if it is still THIS finding's. */
export function releaseFindingFocus(findingId: string): void {
  if (getFindingFocus()?.id === findingId) writeLocal(FOCUS_KEY, null);
}

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    let message = `${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiClientError(response.status, message);
  }

  return (await response.json()) as T;
}

// ------------------------------------------------------------------ config --

export const getConfig = () => request<WidgetConfig>("/api/qa/config");

// ---------------------------------------------------------------- sessions --

export const createSession = (name: string | null) =>
  request<QaSession>("/api/qa/sessions", {
    method: "POST",
    body: JSON.stringify({ name }),
  });

/**
 * The tester's sessions, plus how much each holds.
 *
 * `recorded` / `lastRecordedAt` are optional because this bundle can be newer
 * than the server answering it (see `wireList` in panel.ts) — a deployment that
 * predates them sends sessions alone, and the picker simply shows no counts.
 */
export const listSessions = () =>
  request<{
    sessions: QaSession[];
    recorded?: Record<string, number>;
    lastRecordedAt?: Record<string, string>;
  }>("/api/qa/sessions");

export const getChecklistState = (sessionId: string) =>
  request<{ items: ChecklistState[] }>(
    `/api/qa/sessions/${encodeURIComponent(sessionId)}/items`,
  );

/**
 * `note`, `checked_steps` and `attachment_ids` are PATCH-shaped end to end: a
 * key left out of `patch` is left out of the body, and the server leaves the
 * stored value alone. That is why they are one optional object rather than
 * positional arguments — ticking ✓ must not erase a note, and saving a note
 * must not un-tick a step.
 */
export const setChecklistState = (
  sessionId: string,
  itemId: string,
  status: ChecklistItemStatus,
  routeKey: string | null,
  patch: {
    note?: string | null;
    checked_steps?: number[];
    attachment_ids?: string[];
  } = {},
) =>
  request<ChecklistState>(
    `/api/qa/sessions/${encodeURIComponent(sessionId)}/items`,
    {
      method: "POST",
      body: JSON.stringify({
        item_id: itemId,
        status,
        ...patch,
        // Breadcrumb for resuming: the surface this verdict was given on, and
        // the exact URL that rendered it.
        route_key: routeKey,
        url: location.href,
      }),
    },
  );

// -------------------------------------------------------------- checklists --

/**
 * Scenarios a developer says are fixed and nobody has re-checked. Project-wide
 * on purpose: a fresh retest session has no state of its own, so it cannot know
 * which scenario it exists to re-run.
 */
export const getRetestQueue = () =>
  request<{
    items: {
      item_id: string;
      route_keys: string[];
      finding_id: string | null;
    }[];
  }>("/api/qa/retest");

export const getChecklists = (routeKey: string) =>
  request<{
    route_key: string;
    items: ChecklistItem[];
    other_views: ChecklistItem[];
    global: ChecklistItem[];
  }>(`/api/qa/checklists?route=${encodeURIComponent(routeKey)}`);

// --------------------------------------------------------------- item chat --

/**
 * "What is this scenario asking me to do?"
 *
 * Deliberately NOT outbox-backed, unlike a finding. Rule 8 exists because
 * capture must never block on the network — but a question has no answer
 * offline, so queuing one would promise a reply that never comes. A failed send
 * surfaces the error and leaves the draft in the box; the outbox is for
 * EVIDENCE, and a question is not evidence until it is answered.
 */
export const getItemChat = (
  sessionId: string,
  itemId: string,
  stepIndex: number | null,
) =>
  request<{ thread: ItemChatThread | null }>(
    `/api/qa/item-chat?session=${encodeURIComponent(sessionId)}` +
      `&item=${encodeURIComponent(itemId)}` +
      (stepIndex === null ? "" : `&step=${stepIndex}`),
  );

export const sendItemChat = (
  sessionId: string,
  itemId: string,
  stepIndex: number | null,
  message: string,
) =>
  request<{ thread: ItemChatThread }>("/api/qa/item-chat", {
    method: "POST",
    body: JSON.stringify({
      session_id: sessionId,
      item_id: itemId,
      step_index: stepIndex,
      message,
    }),
  });

// ------------------------------------------------------------- attachments --

export const createAttachment = (body: {
  session_id: string;
  mime: string;
  width: number;
  height: number;
  /** base64, no `data:` prefix. */
  data: string;
}) =>
  request<Attachment>("/api/qa/attachments", {
    method: "POST",
    body: JSON.stringify(body),
  });

/**
 * The bytes. Not `request` — the response is an image, not JSON, and it needs
 * the bearer header, which is exactly why an `<img src>` cannot fetch it
 * directly and the caller turns this blob into an object URL instead.
 */
export async function fetchAttachment(id: string): Promise<Blob> {
  const token = getToken();
  const response = await fetch(
    `${baseUrl}/api/qa/attachments/${encodeURIComponent(id)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!response.ok) {
    throw new ApiClientError(response.status, `${response.status}`);
  }
  return await response.blob();
}

// ---------------------------------------------------------------- findings --

export const listFindings = (routeKey: string) =>
  request<{ findings: Finding[] }>(
    `/api/qa/findings?route=${encodeURIComponent(routeKey)}`,
  );

/**
 * Findings across the whole PROJECT — the "All findings" tab, where a tester
 * reaches every finding wherever they stand. Optionally one status, and the
 * server's text search. Capped at 200 (newest first) by the server.
 */
export const listProjectFindings = (
  opts: { status?: FindingStatus; q?: string; awaitingTester?: boolean } = {},
) => {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  // Open findings where the last word is not the tester's (contract §7i).
  if (opts.awaitingTester) params.set("awaiting", "tester");
  if (opts.q) params.set("q", opts.q);
  const query = params.toString();
  return request<{ findings: Finding[] }>(
    `/api/qa/findings${query ? `?${query}` : ""}`,
  );
};

/**
 * The tester changes a finding's status by hand — the undo for any verdict
 * they gave. `from` is the status the card showed; a finding that moved since
 * is a 409 with a message saying so.
 */
export const setTesterStatus = (
  id: string,
  from: FindingStatus,
  status: TesterStatus,
) =>
  request<{ finding: Finding }>(
    `/api/qa/findings/${encodeURIComponent(id)}/status`,
    { method: "POST", body: JSON.stringify({ from, status }) },
  );

/**
 * Ask for an AI fix — or start one, where the project lets a tester — and the
 * undo (`requested: false`). The server decides which from the project's
 * setting and refuses an agent's token; the tester is the token's.
 */
export const requestAiFix = (id: string, requested: boolean) =>
  request<{
    finding: Finding;
    outcome: "requested" | "queued" | "withdrawn" | "unchanged";
  }>(`/api/qa/findings/${encodeURIComponent(id)}/fix-request`, {
    method: "POST",
    body: JSON.stringify({ requested }),
  });

/**
 * One finding, by id, whatever page the panel is standing on.
 *
 * The list read is route-scoped — right for "what has been filed on this page",
 * useless for the inbox, which names findings filed anywhere in the project and
 * routinely on another ENVIRONMENT (a tester on localhost, a board full of
 * staging findings). The answer the strip asks for lives on the card, so the
 * card comes to the tester rather than the other way round.
 */
export const getFinding = (id: string) =>
  request<{ finding: Finding }>(`/api/qa/findings/${encodeURIComponent(id)}`);

export const patchFinding = (
  id: string,
  patch: {
    status?: FindingStatus;
    severity?: Severity;
    comment?: string | null;
  },
) =>
  request<Finding>(`/api/qa/findings/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

export interface FindingRevisionRow {
  observed: string;
  expected: string | null;
  actor: string;
  at: string;
}

export const getFindingRevisions = (id: string) =>
  request<{ revisions: FindingRevisionRow[] }>(
    `/api/qa/findings/${encodeURIComponent(id)}/revisions`,
  );

/** The author fixing their own wording — see server/findings.ts. */
export const correctFinding = (
  id: string,
  observed: string,
  expected: string | null,
) =>
  request<{ finding: Finding }>(
    `/api/qa/findings/${encodeURIComponent(id)}/correct`,
    { method: "POST", body: JSON.stringify({ observed, expected }) },
  );

// ---------------------------------------------------- finding conversation --

/**
 * The thread on one finding.
 *
 * Deliberately NOT outbox-backed, for the same reason item chat is not: rule 8
 * exists because CAPTURE must never block on the network, and a comment is a
 * message rather than evidence. A failed send leaves the words in the box; the
 * outbox is for findings.
 *
 * Reading a thread also stamps this tester's read mark server-side, which is
 * why opening one is all it takes to clear its row from the inbox strip.
 */
export const listFindingComments = (id: string) =>
  request<{ comments: FindingComment[] }>(
    `/api/qa/findings/${encodeURIComponent(id)}/comments`,
  );

export const addFindingComment = (
  id: string,
  body: string,
  evidence?: { sessionId: string; attachmentIds: string[] },
) =>
  request<{ comment: FindingComment; finding: Finding }>(
    `/api/qa/findings/${encodeURIComponent(id)}/comments`,
    {
      method: "POST",
      body: JSON.stringify({
        body,
        // Sent together or not at all: an attachment is owned by the session
        // that uploaded it, so an id without one has nothing to be checked
        // against and the server drops it.
        ...(evidence?.attachmentIds.length
          ? {
              session_id: evidence.sessionId,
              attachment_ids: evidence.attachmentIds,
            }
          : {}),
      }),
    },
  );

/**
 * "Has anything happened since I was last here?"
 *
 * The shapes are spelled out here rather than imported from the contract
 * because the widget bundle carries no schemas (rule 6) — the same reason
 * `getRetestQueue` declares its own. Scoped to the TESTER, not the session: a
 * sweep is a session, an inbox is a person's.
 */
export interface TesterInbox {
  seen_at: string | null;
  replies: {
    finding_id: string;
    url: string;
    route_key: string;
    item_id: string | null;
    unread: number;
    latest: { author: string; body: string; created_at: string };
  }[];
  retests: {
    finding_id: string;
    /**
     * The scenario to re-run, or null when the finding was filed
     * free-standing. Nullable because the panel already has "Verified fixed" /
     * "Still broken" on exactly that card — the tester was the only person who
     * could answer the claim and the only one never told about it.
     */
    item_id: string | null;
    /** Empty for a free-standing finding: there is no scenario, so the
     *  finding's own URL is the way back. */
    route_keys: string[];
    url: string;
    fixed_at: string;
    fix_note: string | null;
    /**
     * The build the fixer says it landed in, and the one the finding was filed
     * on. Both optional on the wire, not just nullable: a server older than
     * this widget sends neither, and the row then reads exactly as it did
     * before builds existed.
     */
    fixed_in_build?: string | null;
    filed_in_build?: string | null;
    /** Where to retest when it is not `url` — a fix runner's live preview
     *  (§7k). Optional: an older server sends none. */
    retest_url?: string | null;
  }[];
  /**
   * Questions waiting for this tester's view (`needs_discussion`). Optional on
   * the wire: a server older than the status sends none, which means "nothing
   * asked". `reminder` is set once one has waited longer than a day.
   */
  discussions?: {
    finding_id: string;
    url: string;
    route_key: string;
    item_id: string | null;
    asked_at: string;
    asked_by: string | null;
    question: string | null;
    reminder: boolean;
  }[];
  /**
   * Open findings whose last word is a developer's or an agent's — the
   * tester's turn, read or not. Not dismissable: answering is the way off.
   * Optional on the wire: an older server sends none.
   */
  answers?: {
    finding_id: string;
    url: string;
    route_key: string;
    item_id: string | null;
    latest: { author: string; body: string; created_at: string };
  }[];
  /**
   * Claims this tester dismissed and can bring back. Both optional on the
   * wire: an older server sends neither, and the strip then has no "n
   * dismissed" to offer — which is exactly what it had before.
   */
  dismissed_count?: number;
  /** Only filled when asked for (`getInbox(true)`). */
  dismissed?: TesterInbox["retests"];
  /** Questions ✕ snoozed, with their way back. Only when asked for, and
   *  absent from a server older than the undo. */
  dismissed_discussions?: TesterInbox["discussions"];
  checklist: { generatedAt: string | null; changed: boolean };
}

export const getInbox = (includeDismissed = false) =>
  request<TesterInbox>(
    `/api/qa/inbox${includeDismissed ? "?dismissed=1" : ""}`,
  );

/** The undo for ✕: the named claims are news again. */
export const restoreInboxRetests = (findingIds: string[]) =>
  request<{ restored: number }>("/api/qa/inbox/restore", {
    method: "POST",
    body: JSON.stringify({ finding_ids: findingIds }),
  });

/**
 * Dismissing the strip. A thread's own rows are cleared by READING them.
 *
 * Names the fix claims the strip was holding, and only those stop being news:
 * the strip shows a page of claims at most, and a dismissal that moved one
 * clock on the server hid the page AND every claim behind it the tester had
 * never been shown. Claims that arrive later, or were never shown, come back.
 */
export const markInboxSeen = (
  findingIds: string[],
  // Questions are SNOOZED by the same press, not dismissed: each one is back
  // a day later until the tester answers it.
  discussionIds: string[] = [],
) =>
  request<{ seen_at: string }>("/api/qa/inbox/seen", {
    method: "POST",
    body: JSON.stringify({
      finding_ids: findingIds,
      discussion_ids: discussionIds,
    }),
  });

/**
 * The tester's verdict on a question: go ahead, or not needed. Words and
 * screenshots ride along as the tester's own message in the thread; replying
 * in the thread instead keeps the question open.
 */
export const answerDiscussion = (
  id: string,
  verdict: "agree" | "drop",
  body: string,
  evidence?: { sessionId: string; attachmentIds: string[] },
) =>
  request<{ finding: Finding }>(
    `/api/qa/findings/${encodeURIComponent(id)}/discussion/answer`,
    {
      method: "POST",
      body: JSON.stringify({
        verdict,
        ...(body ? { body } : {}),
        ...(evidence?.attachmentIds.length
          ? {
              session_id: evidence.sessionId,
              attachment_ids: evidence.attachmentIds,
            }
          : {}),
      }),
    },
  );

export interface IssueTargetSelection {
  assignee: string | null;
  iteration: string | null;
  parentId: string | null;
  workItemType?: string | null;
}
export interface IssueTargetOption {
  id: string;
  label: string;
}

/** People, sprints and stories, plus what this project filed against last. */
export const getIssueTargets = () =>
  request<{
    targets: {
      assignees: IssueTargetOption[];
      iterations: IssueTargetOption[];
      parents: IssueTargetOption[];
      types: IssueTargetOption[];
    } | null;
    defaults: Partial<IssueTargetSelection>;
  }>("/api/qa/issue-targets");

export const createIssueParent = (title: string) =>
  request<{ parent: IssueTargetOption }>("/api/qa/issue-targets", {
    method: "POST",
    body: JSON.stringify({ title }),
  });

export const fileIssue = (id: string, targets?: IssueTargetSelection) =>
  request<{
    finding: Finding;
    issue: { id: string; url: string };
    created: boolean;
  }>(`/api/qa/findings/${encodeURIComponent(id)}/file-issue`, {
    method: "POST",
    body: JSON.stringify(targets ?? {}),
  });

/** Re-target an issue already filed — no duplicate in the customer's tracker. */
export const updateIssueTargets = (id: string, targets: IssueTargetSelection) =>
  request<{ finding: Finding }>(
    `/api/qa/findings/${encodeURIComponent(id)}/issue-targets`,
    { method: "POST", body: JSON.stringify(targets) },
  );

// ------------------------------------------------------------------ outbox --

function readOutbox(): CreateFindingBody[] {
  const raw = readLocal(OUTBOX_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as CreateFindingBody[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeOutbox(items: CreateFindingBody[]): void {
  writeLocal(OUTBOX_KEY, items.length ? JSON.stringify(items) : null);
}

export function outboxSize(): number {
  return readOutbox().length;
}

/**
 * Create a finding. On transport failure the finding is queued and `null` is
 * returned — the caller treats that as "captured, not yet synced".
 */
export async function createFinding(
  body: CreateFindingBody,
): Promise<Finding | null> {
  try {
    return await request<Finding>("/api/qa/findings", {
      method: "POST",
      body: JSON.stringify(body),
    });
  } catch (error) {
    // A rejected request (4xx) is a real failure the tester must see; only
    // transport failures are queued.
    if (error instanceof ApiClientError) throw error;
    writeOutbox([...readOutbox(), body]);
    return null;
  }
}

/** Flush queued findings. Returns how many are still queued afterwards. */
export async function flushOutbox(): Promise<number> {
  const queued = readOutbox();
  if (!queued.length) return 0;

  const remaining: CreateFindingBody[] = [];
  for (const body of queued) {
    try {
      await request<Finding>("/api/qa/findings", {
        method: "POST",
        body: JSON.stringify(body),
      });
    } catch (error) {
      // 4xx ⇒ this finding will never succeed (bad session, revoked token);
      // drop it rather than blocking the queue forever, but say so.
      if (error instanceof ApiClientError && error.status < 500) {
        console.warn(
          "[qa-deck] dropping unsendable queued finding",
          error.message,
          body,
        );
        continue;
      }
      remaining.push(body);
    }
  }
  writeOutbox(remaining);
  return remaining.length;
}
